/**
 * NuShellRail — the fixed side rail: brand row (flush with the 52px glass
 * toolbar), collapse toggle, flat expanded nav with nested sub-pages, labeled
 * ADMINISTRATION section, footer items + theme toggle.
 *
 * Ported verbatim from NU-DATA-DASHBOARD/kits/_core/shell.jsx
 * (NuShellRail / NuShellNavItem / NuShellNavExpandable / NuShellNavGroup /
 * NuShellThemeToggle): inline style objects reference var(--nu-*) tokens and
 * the shared .nu-* class library only — no raw hex, no Tailwind re-authoring.
 * Route strings are this SPA's BrowserRouter paths (see navConfig.ts).
 * Nav data arrives PRE-FILTERED by role (visibleNavForRole) — the rail only
 * renders what it is given.
 */

import React, { useEffect, useRef, useState, type ReactNode } from "react";
import { NuIcons } from "../shared/icons";
import { NuBrandIcon, NuBrandLogo } from "./BrandLogo";
import type { NavigateFn, ShellNavGroup, ShellNavItem } from "./navConfig";

export type ShellTheme = "light" | "dark";

/* ── collapsed-rail tooltip (kit side-effect pattern, verbatim) ── */

function useRailTooltip(
  btnRef: React.RefObject<HTMLButtonElement | null>,
  hover: boolean,
  collapsed: boolean,
  label: string,
) {
  useEffect(() => {
    if (!collapsed || !hover || !btnRef.current) {
      const tip = document.getElementById("nu-rail-tooltip");
      if (tip) tip.removeAttribute("data-show");
      return;
    }
    let tip = document.getElementById("nu-rail-tooltip");
    if (!tip) {
      tip = document.createElement("div");
      tip.id = "nu-rail-tooltip";
      tip.className = "nu-rail-tooltip";
      document.body.appendChild(tip);
    }
    const r = btnRef.current.getBoundingClientRect();
    tip.textContent = label;
    tip.style.left = `${r.right + 12}px`;
    tip.style.top = `${r.top + r.height / 2}px`;
    tip.setAttribute("data-show", "1");
  }, [btnRef, hover, collapsed, label]);
}

/* ── hover via native listeners (kit belt-and-braces pattern) ── */

function useNativeHover(btnRef: React.RefObject<HTMLButtonElement | null>) {
  const [hover, setHover] = useState(false);
  useEffect(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const on = () => setHover(true);
    const off = () => setHover(false);
    btn.addEventListener("mouseenter", on);
    btn.addEventListener("mouseleave", off);
    btn.addEventListener("focus", on);
    btn.addEventListener("blur", off);
    return () => {
      btn.removeEventListener("mouseenter", on);
      btn.removeEventListener("mouseleave", off);
      btn.removeEventListener("focus", on);
      btn.removeEventListener("blur", off);
    };
  }, [btnRef]);
  return hover;
}

/* ── NuShellNavItem ── */

interface NavItemProps {
  item: ShellNavItem;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
}

function NuShellNavItem({ item, active, collapsed, onClick }: NavItemProps) {
  const Icon = item.icon ? NuIcons[item.icon] : undefined;
  const btnRef = useRef<HTMLButtonElement>(null);
  const hover = useNativeHover(btnRef);
  useRailTooltip(btnRef, hover, collapsed, item.label);

  const baseStyle: React.CSSProperties = collapsed
    ? { width: 40, height: 40, padding: 0, justifyContent: "center", gap: 0 }
    : {};
  const hoverStyle: React.CSSProperties = hover
    ? {
        background: active
          ? "color-mix(in srgb, var(--nu-primary) 24%, transparent)"
          : collapsed
            ? "var(--nu-row-hover)"
            : "var(--nu-surface-alt)",
        color: "var(--nu-text)",
        boxShadow: collapsed
          ? "inset 0 0 0 1px var(--nu-border-strong)"
          : undefined,
      }
    : {};
  return (
    <button
      ref={btnRef}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`nu-nav-item ${collapsed ? "nu-nav-item--collapsed" : ""} ${active ? "nu-nav-item--active" : ""}`}
      style={{
        ...baseStyle,
        ...hoverStyle,
        transition:
          "background 120ms ease, color 120ms ease, box-shadow 120ms ease",
      }}
    >
      {Icon && (
        <span className="nu-nav-item__icon">
          <Icon />
        </span>
      )}
      {!collapsed && (
        <span
          style={{
            flex: 1,
            textAlign: "left",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.label}
        </span>
      )}
    </button>
  );
}

/* ── NuShellThemeToggle ── */

interface ThemeToggleProps {
  theme: ShellTheme;
  onToggleTheme?: () => void;
  collapsed: boolean;
}

function NuShellThemeToggle({
  theme,
  onToggleTheme,
  collapsed,
}: ThemeToggleProps) {
  const label = theme === "dark" ? "Light mode" : "Dark mode";
  const btnRef = useRef<HTMLButtonElement>(null);
  const hover = useNativeHover(btnRef);
  useRailTooltip(btnRef, hover, collapsed, label);
  if (!onToggleTheme) return null;

  const baseStyle: React.CSSProperties = collapsed
    ? { width: 40, height: 40, padding: 0, justifyContent: "center", gap: 0 }
    : {};
  const hoverStyle: React.CSSProperties = hover
    ? {
        background: collapsed ? "var(--nu-row-hover)" : "var(--nu-surface-alt)",
        color: "var(--nu-text)",
        boxShadow: collapsed
          ? "inset 0 0 0 1px var(--nu-border-strong)"
          : undefined,
      }
    : {};
  return (
    <button
      ref={btnRef}
      onClick={onToggleTheme}
      className={`nu-nav-item ${collapsed ? "nu-nav-item--collapsed" : ""}`}
      aria-label="Toggle theme"
      style={{
        ...baseStyle,
        ...hoverStyle,
        transition:
          "background 120ms ease, color 120ms ease, box-shadow 120ms ease",
      }}
    >
      <span className="nu-nav-item__icon">
        {theme === "dark" ? (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
          </svg>
        ) : (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        )}
      </span>
      {!collapsed && <span>{label}</span>}
    </button>
  );
}

/* ── NuShellNavExpandable — parent row + indented sub-pages ── */

interface NavExpandableProps {
  item: ShellNavItem;
  route: string;
  collapsed: boolean;
  onNavigate: NavigateFn;
}

function NuShellNavExpandable({
  item,
  route,
  collapsed,
  onNavigate,
}: NavExpandableProps) {
  const children = item.children ?? [];
  const selfActive = route === item.route;
  const childActive = children.some((c) => c.route === route);
  const [open, setOpen] = useState(selfActive || childActive);
  useEffect(() => {
    if (selfActive || childActive) setOpen(true);
  }, [selfActive, childActive]);

  // Collapsed rail → icon only (sub-pages reached via in-page sub-nav).
  if (collapsed) {
    return (
      <NuShellNavItem
        item={item}
        active={selfActive || childActive}
        collapsed
        onClick={() => onNavigate(item.route)}
      />
    );
  }

  const Icon = item.icon ? NuIcons[item.icon] : undefined;
  return (
    <div>
      <button
        onClick={() => {
          onNavigate(item.route);
          setOpen(true);
        }}
        aria-current={selfActive ? "page" : undefined}
        aria-expanded={open ? "true" : "false"}
        className={`nu-nav-item ${selfActive ? "nu-nav-item--active" : ""}`}
        style={{ width: "100%", justifyContent: "flex-start" }}
      >
        {Icon && (
          <span className="nu-nav-item__icon">
            <Icon />
          </span>
        )}
        <span style={{ flex: 1, textAlign: "left" }}>{item.label}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
          style={{
            flexShrink: 0,
            opacity: 0.55,
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 180ms",
          }}
        >
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>
      {open && (
        <div
          style={{
            position: "relative",
            margin: "2px 0 4px 0",
            paddingLeft: 26,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 18,
              top: 4,
              bottom: 4,
              width: 1,
              background: "var(--nu-border-strong)",
            }}
          />
          {children.map((c) => {
            const CIcon = c.icon ? NuIcons[c.icon] : undefined;
            const isActive = route === c.route;
            return (
              <button
                key={c.id}
                onClick={() => onNavigate(c.route)}
                aria-current={isActive ? "page" : undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "7px 10px",
                  borderRadius: 6,
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  background: isActive
                    ? "color-mix(in srgb, var(--nu-primary) 14%, transparent)"
                    : "transparent",
                  color: isActive
                    ? "var(--nu-primary)"
                    : "var(--nu-text-muted)",
                  font: "500 12.5px/1.2 var(--nu-font-sans)",
                  transition: "background 100ms, color 100ms",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "var(--nu-surface-alt)";
                    e.currentTarget.style.color = "var(--nu-text)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "var(--nu-text-muted)";
                  }
                }}
              >
                {CIcon && (
                  <span
                    style={{
                      flexShrink: 0,
                      display: "inline-flex",
                      color: isActive
                        ? "var(--nu-primary)"
                        : "var(--nu-text-subtle)",
                    }}
                  >
                    <CIcon size={14} />
                  </span>
                )}
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {c.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── NuShellNavGroup — collapsed-rail flyout for labeled groups ── */

interface NavGroupProps {
  group: ShellNavGroup;
  route: string;
  collapsed: boolean;
  onNavigate: NavigateFn;
  pinned: boolean;
  onTogglePin: () => void;
}

function NuShellNavGroup({
  group,
  route,
  collapsed,
  onNavigate,
  pinned,
  onTogglePin,
}: NavGroupProps) {
  const items = group.items ?? [];
  const activeInGroup = items.some((it) => it.route === route);
  const firstIconName = items.find((it) => it.icon)?.icon;
  const Icon = firstIconName ? NuIcons[firstIconName] : NuIcons.list;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pinnedRef = useRef(pinned);
  pinnedRef.current = pinned;

  function openNow() {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  }
  function closeSoon() {
    if (pinnedRef.current) return; // pinned menus stay open
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 220);
  }
  function togglePin(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    onTogglePin();
  }
  // Pinning forces the flyout open; unpinning collapses it.
  useEffect(() => {
    setOpen(pinned);
  }, [pinned]);
  // Native listeners — React synthetic mouseenter sometimes doesn't fire
  // when the parent has a transform/stacking context.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.addEventListener("mouseenter", openNow);
    el.addEventListener("mouseleave", closeSoon);
    return () => {
      el.removeEventListener("mouseenter", openNow);
      el.removeEventListener("mouseleave", closeSoon);
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);
  useEffect(() => {
    if (!open || pinned) return; // pinned close is owned by the rail
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        const flyout = document.querySelector(
          `[data-nui-flyout="${group.id || group.label}"]`,
        );
        if (flyout && flyout.contains(e.target as Node)) return;
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, pinned, group.id, group.label]);

  const flyoutStyle = (): React.CSSProperties => {
    if (!collapsed) {
      return {
        padding: "4px 0 4px 8px",
        marginBottom: 4,
        animation: "nu-fade-in 110ms ease-out",
      };
    }
    // Fixed coords so the flyout escapes the rail's overflow scroll container.
    const r = wrapRef.current?.getBoundingClientRect();
    return {
      position: "fixed",
      top: r ? r.top : 64,
      left: r ? r.right + 6 : 70,
      minWidth: 240,
      maxHeight: "calc(100vh - 80px)",
      overflowY: "auto",
      background: "var(--nu-surface)",
      border: "1px solid var(--nu-border-strong)",
      borderRadius: 10,
      boxShadow: "0 16px 40px -10px rgb(0 0 0 / .45)",
      padding: 6,
      zIndex: 9999,
      animation: "nu-fade-in 110ms ease-out",
    };
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        onClick={togglePin}
        onFocus={openNow}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        aria-pressed={pinned ? "true" : "false"}
        title={collapsed ? `${group.label} — click to pin` : undefined}
        className={`nu-nav-item ${collapsed ? "nu-nav-item--collapsed" : ""} ${activeInGroup || open ? "nu-nav-item--active" : ""}`}
        style={
          collapsed
            ? {
                width: 40,
                height: 40,
                padding: 0,
                justifyContent: "center",
                gap: 0,
                background: pinned || open ? "var(--nu-row-hover)" : undefined,
                boxShadow: pinned
                  ? "inset 0 0 0 2px var(--nu-primary)"
                  : open
                    ? "inset 0 0 0 1px var(--nu-border-strong)"
                    : undefined,
              }
            : {
                width: "100%",
                justifyContent: "flex-start",
                background:
                  activeInGroup || open ? "var(--nu-row-hover)" : undefined,
                boxShadow: pinned
                  ? "inset 3px 0 0 var(--nu-primary)"
                  : undefined,
              }
        }
      >
        <span className="nu-nav-item__icon">
          <Icon />
        </span>
        {!collapsed && (
          <span style={{ flex: 1, textAlign: "left" }}>{group.label}</span>
        )}
        {!collapsed && items.length > 0 && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              marginLeft: "auto",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 180ms",
              flexShrink: 0,
              opacity: 0.6,
            }}
          >
            <polyline points="9 6 15 12 9 18" />
          </svg>
        )}
      </button>
      {open && (
        <div
          role="menu"
          data-nui-flyout={group.id || group.label}
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
          style={flyoutStyle()}
        >
          {group.label && collapsed && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "6px 10px 8px",
                borderBottom: "1px solid var(--nu-border)",
                marginBottom: 4,
                font: "700 10px/1 var(--nu-font-mono)",
                letterSpacing: ".14em",
                textTransform: "uppercase",
                color: "var(--nu-text-muted)",
              }}
            >
              <span>{group.label}</span>
              {pinned && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "2px 6px",
                    background:
                      "color-mix(in srgb, var(--nu-primary) 14%, transparent)",
                    color: "var(--nu-primary)",
                    borderRadius: 4,
                    fontSize: 9,
                    textTransform: "uppercase",
                  }}
                >
                  <svg
                    width="9"
                    height="9"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M16 4l4 4-7 7v5h-2v-5l-7-7 4-4 4 4z" />
                  </svg>
                  pinned
                </span>
              )}
            </div>
          )}
          {items.map((it) => {
            const ItIcon = it.icon ? NuIcons[it.icon] : undefined;
            const isActive = route === it.route;
            return (
              <button
                key={it.id}
                role="menuitem"
                onClick={() => {
                  onNavigate(it.route);
                  if (!pinned) setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  background: isActive
                    ? "color-mix(in srgb, var(--nu-primary) 14%, transparent)"
                    : "transparent",
                  color: isActive ? "var(--nu-primary)" : "var(--nu-text)",
                  font: "500 13px/1.2 var(--nu-font-sans)",
                  transition: "background 100ms, color 100ms",
                }}
                onMouseEnter={(e) => {
                  if (!isActive)
                    e.currentTarget.style.background = "var(--nu-surface-alt)";
                }}
                onMouseLeave={(e) => {
                  if (!isActive)
                    e.currentTarget.style.background = "transparent";
                }}
              >
                {ItIcon && (
                  <span
                    style={{
                      color: isActive
                        ? "var(--nu-primary)"
                        : "var(--nu-text-subtle)",
                      display: "inline-flex",
                      flexShrink: 0,
                    }}
                  >
                    <ItIcon size={16} />
                  </span>
                )}
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {it.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── NuShellRail ── */

export interface NuShellRailProps {
  route: string;
  onNavigate: NavigateFn;
  theme: ShellTheme;
  onToggleTheme?: () => void;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
  navGroups: readonly ShellNavGroup[];
  navFooter: readonly ShellNavItem[];
  /** Custom brand row content; defaults to the NU DATA wordmark. */
  logo?: ReactNode;
}

export function NuShellRail({
  route,
  onNavigate,
  theme,
  onToggleTheme,
  mobileOpen,
  onCloseMobile,
  navGroups,
  navFooter,
  logo,
}: NuShellRailProps) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("nu-rail-collapsed") === "1";
    } catch {
      return false;
    }
  });
  // Single source of truth for which labeled group is pinned-open (collapsed
  // rail flyouts). Escape unpins; clicking a new group moves the pin.
  const [pinnedGroup, setPinnedGroup] = useState<string | null>(null);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setPinnedGroup(null);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  function toggleCollapse() {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem("nu-rail-collapsed", next ? "1" : "0");
      } catch {
        /* storage unavailable */
      }
      return next;
    });
  }

  function go(r: string) {
    onNavigate(r);
    onCloseMobile?.();
  }

  return (
    <nav
      className="nu-rail"
      aria-label="Primary"
      data-open={mobileOpen ? "true" : undefined}
      data-collapsed={collapsed ? "true" : undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        width: collapsed ? 64 : 256,
        padding: collapsed ? "0 8px 16px" : undefined,
        transition:
          "width 220ms cubic-bezier(.16,1,.3,1), padding 220ms cubic-bezier(.16,1,.3,1)",
        position: "relative",
      }}
    >
      {/* FIXED top — brand row is EXACTLY 52px in both states so its divider
          is continuous with the top toolbar's; the collapse toggle gets its
          own row below when collapsed. */}
      <div
        className="nu-rail__logo"
        style={{
          flex: "none",
          height: 52,
          boxSizing: "border-box",
          padding: 0,
          display: "flex",
          alignItems: "center",
          gap: 8,
          justifyContent: collapsed ? "center" : "space-between",
          borderBottom: "1px solid var(--nu-border-strong)",
          marginBottom: collapsed ? 0 : 8,
        }}
      >
        {collapsed ? (
          <NuBrandIcon mode={theme} size={28} />
        ) : (
          <span
            style={{ display: "inline-flex", minWidth: 0, overflow: "hidden" }}
          >
            {logo || <NuBrandLogo mode={theme} width={150} />}
          </span>
        )}
        {!collapsed && (
          <button
            onClick={toggleCollapse}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              flexShrink: 0,
              background: "var(--nu-primary)",
              border: "2px solid var(--nu-surface)",
              boxShadow:
                "0 4px 12px -2px rgba(0,0,0,0.4), 0 0 0 1px var(--nu-border-strong)",
              color: "var(--nu-on-primary)",
              cursor: "pointer",
              display: "grid",
              placeItems: "center",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: "rotate(180deg)",
                transition: "transform 220ms",
              }}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        )}
      </div>
      {collapsed && (
        <button
          onClick={toggleCollapse}
          aria-label="Expand sidebar"
          title="Expand sidebar"
          style={{
            flex: "none",
            width: "100%",
            height: 32,
            margin: "8px 0 0",
            display: "grid",
            placeItems: "center",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "var(--nu-text-muted)",
            borderRadius: "var(--nu-radius-control)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--nu-surface-alt)";
            e.currentTarget.style.color = "var(--nu-text)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "var(--nu-text-muted)";
          }}
        >
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: 999,
              display: "grid",
              placeItems: "center",
              background: "var(--nu-primary)",
              color: "var(--nu-on-primary)",
              boxShadow: "0 4px 12px -2px rgba(0,0,0,0.4)",
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </span>
        </button>
      )}

      {/* SCROLLABLE middle */}
      <div
        className="nu-rail__scroll"
        style={{
          flex: "1 1 auto",
          overflowY: "auto",
          overflowX: "visible",
          minHeight: 0,
          scrollbarWidth: "thin",
          scrollbarColor: "rgb(255 255 255 / .15) transparent",
        }}
      >
        {navGroups.map((g, gi) => {
          if (!g.items || g.items.length === 0) return null;
          if (g.items.length === 1 && !g.label) {
            const it = g.items[0];
            return (
              <NuShellNavItem
                key={it.id}
                item={it}
                active={route === it.route}
                collapsed={collapsed}
                onClick={() => go(it.route)}
              />
            );
          }
          const gid = g.id || String(gi);
          // Labelless multi-item group (primary nav) → flat EXPANDED list,
          // items with sub-pages nested beneath. No flyout.
          if (!g.label) {
            return (
              <div key={gid} className="nu-rail__list">
                {g.items.map((it) =>
                  it.children && it.children.length ? (
                    <NuShellNavExpandable
                      key={it.id}
                      item={it}
                      route={route}
                      collapsed={collapsed}
                      onNavigate={go}
                    />
                  ) : (
                    <NuShellNavItem
                      key={it.id}
                      item={it}
                      active={route === it.route}
                      collapsed={collapsed}
                      onClick={() => go(it.route)}
                    />
                  ),
                )}
              </div>
            );
          }
          // Labeled group (Administration): expanded section with a header
          // when the rail is open; compact flyout when collapsed.
          if (!collapsed) {
            return (
              <div
                key={gid}
                className="nu-rail__list"
                style={{ marginTop: 10 }}
              >
                <div
                  style={{
                    padding: "6px 14px 4px",
                    font: "700 10px/1 var(--nu-font-mono)",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    color: "var(--nu-text-subtle)",
                  }}
                >
                  {g.label}
                </div>
                {g.items.map((it) =>
                  it.children && it.children.length ? (
                    <NuShellNavExpandable
                      key={it.id}
                      item={it}
                      route={route}
                      collapsed={false}
                      onNavigate={go}
                    />
                  ) : (
                    <NuShellNavItem
                      key={it.id}
                      item={it}
                      active={route === it.route}
                      collapsed={false}
                      onClick={() => go(it.route)}
                    />
                  ),
                )}
              </div>
            );
          }
          return (
            <NuShellNavGroup
              key={gid}
              group={g}
              route={route}
              collapsed={collapsed}
              pinned={pinnedGroup === gid}
              onTogglePin={() =>
                setPinnedGroup((p) => (p === gid ? null : gid))
              }
              onNavigate={go}
            />
          );
        })}
      </div>

      {/* FIXED bottom — footer items + theme toggle always visible. */}
      <div
        className="nu-rail__footer"
        style={{
          flex: "none",
          borderTop: "1px solid var(--nu-border)",
          padding: "6px 0 8px",
        }}
      >
        <div className="nu-rail__list">
          {navFooter.map((it) => (
            <NuShellNavItem
              key={it.id}
              item={it}
              active={route === it.route}
              collapsed={collapsed}
              onClick={() => go(it.route)}
            />
          ))}
          <NuShellThemeToggle
            theme={theme}
            onToggleTheme={onToggleTheme}
            collapsed={collapsed}
          />
        </div>
      </div>
    </nav>
  );
}

export default NuShellRail;
